/**
 * Inventory Insights — two tabs.
 * Stock Health: all ingredients with stock vs threshold, status badge.
 * Transactions: restock + depletion log with totals per ingredient.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, X } from 'lucide-react';
import { WASTE_REASONS, normalizeWasteReason, type WasteReason } from '@cafe-pos/shared';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  type FlowPeriod,
  enumerateDays,
  fmtNum,
  getPeriodDates,
  isUuid,
  parseWasteNote,
  periodToIsoRange,
  toDayKey,
} from '../inventoryPeriod.js';
import type { Database } from '@cafe-pos/supabase';
import { formatAmd, resolveAppLocale } from '../locale.js';
import { supabase } from '../supabase.js';
import { Button, Callout, ConfirmDialog, DateRange, Field, IconButton, Modal, PageHeader, Tabs } from '../ui/index.js';

type InvTxType = Database['public']['Tables']['inventory_transactions']['Row']['type'];

const TX_TYPE_I18N: Record<InvTxType, string> = {
  sale: 'inventoryInsights.typeSale',
  restock: 'inventoryInsights.typeRestock',
  adjustment: 'inventoryInsights.typeAdjustment',
  waste: 'inventoryInsights.typeWaste',
  stock_count: 'inventoryInsights.typeStockCount',
  stock_count_revert: 'inventoryInsights.typeCountRevert',
  waste_revert: 'inventoryInsights.typeWasteRevert',
  invoice: 'inventoryInsights.typeInvoice',
  invoice_revert: 'inventoryInsights.typeInvoiceRevert',
  prep: 'inventoryInsights.typePrep',
  force_close: 'inventoryInsights.typeForceClose',
};

/** PostgREST caps responses at 1000 rows — paginate in 1000-row chunks. */
const FETCH_PAGE = 1000;

async function fetchPaged<T>(
  query: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let offset = 0; ; offset += FETCH_PAGE) {
    const { data, error } = await query(offset, offset + FETCH_PAGE - 1);
    if (error) throw new Error(error.message);
    const batch = data ?? [];
    rows.push(...batch);
    if (batch.length < FETCH_PAGE) break;
  }
  return rows;
}

function lossPct(n: number, d: number): number {
  return d > 0 ? Math.round((n / d) * 1000) / 10 : 0;
}

function fmtDtLocal(iso: string, locale: string) {
  return (
    new Date(iso).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' +
    new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  );
}

type InvTab = 'stock' | 'transactions' | 'flow' | 'loss';

// ─── Types ────────────────────────────────────────────────────────────────────

interface IngredientRow {
  id: string;
  name: string;
  unit: string;
  stock: number;
  threshold: number | null;
  status: 'ok' | 'low' | 'critical';
}

interface TxRow {
  id: string;
  ingredientId: string;
  ingredientName: string;
  type: string;
  delta: number;
  unit: string;
  note: string | null;
  createdAt: string;
}

interface DepletionStat {
  name: string;
  depleted: number;
  unit: string;
}

interface InvData {
  ingredients: IngredientRow[];
  depletion: DepletionStat[];
}

// ─── Fetch ────────────────────────────────────────────────────────────────────

async function fetchInventory(clientId: string): Promise<InvData> {
  if (!supabase) throw new Error('Not configured');
  const db = supabase;

  const [ingRes, depTxs] = await Promise.all([
    db
      .from('ingredients')
      .select('id, name, unit, stock, low_stock_threshold')
      .eq('client_id', clientId)
      .order('name'),

    fetchPaged((offset, limit) =>
      db
        .from('inventory_transactions')
        .select('ingredient_id, type, delta')
        .eq('client_id', clientId)
        .in('type', ['sale', 'adjustment'])
        .order('created_at', { ascending: true })
        .range(offset, limit),
    ),
  ]);

  if (ingRes.error) throw new Error(ingRes.error.message);
  const ings = ingRes.data ?? [];

  // Map ingredient id → ingredient
  const ingMap = new Map(ings.map((i) => [i.id, i]));

  const ingredients: IngredientRow[] = ings.map((i) => {
    const stock = Number(i.stock ?? 0);
    const raw = (i as Record<string, unknown>)['low_stock_threshold'];
    const threshold = raw != null ? Number(raw) : null;
    let status: 'ok' | 'low' | 'critical' = 'ok';
    if (threshold != null) {
      if (stock <= 0) status = 'critical';
      else if (stock <= threshold) status = 'low';
    }
    return { id: i.id, name: i.name, unit: i.unit ?? '', stock, threshold, status };
  });

  // Depletion stats from sale / negative adjustment transactions
  const depMap = new Map<string, number>();
  for (const tx of depTxs) {
    const txId = tx.ingredient_id as string;
    if (tx.type === 'sale' || (tx.type === 'adjustment' && Number(tx.delta) < 0)) {
      depMap.set(txId, (depMap.get(txId) ?? 0) + Math.abs(Number(tx.delta)));
    }
  }
  const depletion: DepletionStat[] = Array.from(depMap, ([id, depleted]) => {
    const ing = ingMap.get(id);
    return { name: ing?.name ?? `#${id}`, depleted, unit: ing?.unit ?? '' };
  })
    .sort((a, b) => b.depleted - a.depleted)
    .slice(0, 12);

  return { ingredients, depletion };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StockHealthTab({
  ingredients,
  depletion,
}: {
  ingredients: IngredientRow[];
  depletion: DepletionStat[];
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<'all' | 'low' | 'critical'>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  const counts = {
    ok: ingredients.filter((i) => i.status === 'ok').length,
    low: ingredients.filter((i) => i.status === 'low').length,
    critical: ingredients.filter((i) => i.status === 'critical').length,
  };

  const visible = ingredients.filter((i) => {
    if (filter !== 'all' && i.status !== filter) return false;
    if (search && !i.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [filter, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const chartData = depletion.map((d) => ({ name: d.name, value: d.depleted }));

  return (
    <div className="insights-grid">
      {/* Summary badges */}
      <div className="chart-card insights-wide">
        <div className="chart-title">{t('inventoryInsights.stockOverview')}</div>
        <div className="stock-summary">
          <button
            className={`stock-badge-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            <span className="badge badge-muted">
              {t('inventoryInsights.totalCount', { count: ingredients.length })}
            </span>
          </button>
          <button
            className={`stock-badge-btn ${filter === 'low' ? 'active' : ''}`}
            onClick={() => setFilter(filter === 'low' ? 'all' : 'low')}
          >
            <span className="badge badge-warning">
              {t('inventoryInsights.lowCount', { count: counts.low })}
            </span>
          </button>
          <button
            className={`stock-badge-btn ${filter === 'critical' ? 'active' : ''}`}
            onClick={() => setFilter(filter === 'critical' ? 'all' : 'critical')}
          >
            <span className="badge badge-danger">
              {t('inventoryInsights.criticalCount', { count: counts.critical })}
            </span>
          </button>
          <input
            type="search"
            placeholder={t('inventoryInsights.searchIngredient')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              marginLeft: 'auto',
              padding: '4px 10px',
              borderRadius: 'var(--r)',
              border: '1px solid var(--border)',
              background: 'var(--sb)',
              color: 'var(--text)',
              fontSize: 13,
            }}
          />
        </div>

        <table className="table" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>{t('inventoryInsights.colIngredient')}</th>
              <th style={{ textAlign: 'right' }}>{t('inventoryInsights.colStock')}</th>
              <th style={{ textAlign: 'right' }}>{t('inventoryInsights.colThreshold')}</th>
              <th>{t('inventoryInsights.colStatus')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((i) => (
              <tr key={i.id}>
                <td style={{ fontWeight: 500 }}>{i.name}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtNum(i.stock)} <span className="muted">{i.unit}</span>
                </td>
                <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>
                  {i.threshold != null
                    ? `${fmtNum(i.threshold)} ${i.unit}`
                    : t('common.emptyDash')}
                </td>
                <td>
                  {i.status === 'ok' && (
                    <span className="badge badge-success">{t('inventoryInsights.statusOk')}</span>
                  )}
                  {i.status === 'low' && (
                    <span className="badge badge-warning">{t('inventoryInsights.statusLow')}</span>
                  )}
                  {i.status === 'critical' && (
                    <span className="badge badge-danger">{t('inventoryInsights.statusCritical')}</span>
                  )}
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  {t('inventoryInsights.noResults')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {visible.length > PAGE_SIZE && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 0 4px',
              justifyContent: 'flex-end',
            }}
          >
            <button
              className="btn-ghost btn-sm"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              {t('common.prev')}
            </button>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {t('common.pageOf', { page: page + 1, count: Math.ceil(visible.length / PAGE_SIZE) })}
            </span>
            <button
              className="btn-ghost btn-sm"
              disabled={(page + 1) * PAGE_SIZE >= visible.length}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>

      {/* Top depletion chart */}
      {depletion.length > 0 && (
        <div className="chart-card">
          <div className="chart-title">{t('inventoryInsights.mostDepleted')}</div>
          <ResponsiveContainer width="100%" height={Math.max(160, depletion.length * 32)}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 4, right: 60, left: 8, bottom: 0 }}
            >
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                width={110}
                tick={{ fontSize: 12, fill: 'var(--text-muted)' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                formatter={(v: unknown) => {
                  return [`${fmtNum(Number(v))}`, t('inventoryInsights.depleted')];
                }}
                contentStyle={TS}
                cursor={{ fill: 'var(--surface-hover)' }}
              />
              <Bar
                dataKey="value"
                fill="var(--danger)"
                radius={[0, 4, 4, 0]}
                maxBarSize={22}
                label={{
                  position: 'right',
                  fontSize: 11,
                  fill: 'var(--text-muted)',
                  formatter: (v: unknown) => fmtNum(Number(v)),
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

const TX_FILTER_TYPES = Object.keys(TX_TYPE_I18N) as InvTxType[];

const TS: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 12,
  color: 'var(--text)',
  boxShadow: 'var(--shadow-sm)',
};

function PeriodFilters({
  period,
  setPeriod,
  customFrom,
  setCustomFrom,
  customTo,
  setCustomTo,
  ingredientId,
  setIngredientId,
  ingredients,
  extra,
}: {
  period: FlowPeriod;
  setPeriod: (p: FlowPeriod) => void;
  customFrom: string;
  setCustomFrom: (v: string) => void;
  customTo: string;
  setCustomTo: (v: string) => void;
  ingredientId: string;
  setIngredientId: (v: string) => void;
  ingredients: { id: string; name: string }[];
  extra?: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        marginBottom: 12,
      }}
    >
      <DateRange
        value={{ preset: period, from: customFrom, to: customTo }}
        onChange={(v) => {
          setPeriod(v.preset);
          setCustomFrom(v.from ?? '');
          setCustomTo(v.to ?? '');
        }}
      />
      <select
        className="input"
        value={ingredientId}
        onChange={(e) => setIngredientId(e.target.value)}
        style={{ minWidth: 160, maxWidth: 220 }}
      >
        <option value="">{t('inventoryInsights.allIngredients')}</option>
        {ingredients.map((i) => (
          <option key={i.id} value={i.id}>
            {i.name}
          </option>
        ))}
      </select>
      {extra}
    </div>
  );
}

function TransactionsTab({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const txTypeLabel = (type: string) => {
    const key = TX_TYPE_I18N[type as InvTxType];
    return key ? t(key) : type;
  };
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<TxRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const PAGE_SIZE = 25;

  const typeOptions = ['all', ...TX_FILTER_TYPES] as const;

  useEffect(() => {
    setPage(0);
  }, [typeFilter]);

  useEffect(() => {
    if (!supabase || !clientId) return;

    setLoading(true);
    setFetchError(null);

    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let query = supabase
      .from('inventory_transactions')
      .select(
        'id, ingredient_id, type, delta, note, created_at, ingredients(name, unit)',
        { count: 'exact' },
      )
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });

    if (typeFilter !== 'all') {
      query = query.eq('type', typeFilter as InvTxType);
    }

    void query.range(from, to).then(({ data, error, count }) => {
      setLoading(false);
      if (error) {
        setFetchError(error.message);
        return;
      }

      type TxDbRow = {
        id: string;
        ingredient_id: string;
        type: string;
        delta: number;
        note: string | null;
        created_at: string;
        ingredients: { name: string; unit: string } | null;
      };

      setRows(
        ((data ?? []) as unknown as TxDbRow[]).map((tx) => ({
          id: tx.id,
          ingredientId: tx.ingredient_id,
          ingredientName: tx.ingredients?.name ?? `#${tx.ingredient_id}`,
          type: tx.type ?? 'unknown',
          delta: Number(tx.delta),
          unit: tx.ingredients?.unit ?? '',
          note: tx.note ?? null,
          createdAt: tx.created_at,
        })),
      );
      setTotalCount(count ?? 0);
    });
  }, [clientId, page, typeFilter]);

  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className="chart-card" style={{ marginTop: 0 }}>
      <div
        className="chart-title"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span>{t('inventoryInsights.txLog', { count: totalCount.toLocaleString(locale) })}</span>
        <div className="segment-ctrl" style={{ fontSize: 11 }}>
          {typeOptions.map((opt) => (
            <button
              key={opt}
              className={typeFilter === opt ? 'active' : ''}
              onClick={() => setTypeFilter(opt)}
            >
              {opt === 'all' ? t('common.all') : txTypeLabel(opt)}
            </button>
          ))}
        </div>
      </div>
      {fetchError && <div className="error-banner">{fetchError}</div>}
      {loading ? (
        <div style={{ padding: 16, color: 'var(--text-muted)' }}>{t('common.loading')}</div>
      ) : (
        <>
          <table className="table" style={{ marginTop: -4 }}>
            <thead>
              <tr>
                <th>{t('inventoryInsights.colDate')}</th>
                <th>{t('inventoryInsights.colIngredient')}</th>
                <th>{t('inventoryInsights.colType')}</th>
                <th style={{ textAlign: 'right' }}>{t('inventoryInsights.colDelta')}</th>
                <th>{t('inventoryInsights.colNote')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((tx) => (
                <tr key={tx.id}>
                  <td>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {fmtDtLocal(tx.createdAt, locale)}
                    </span>
                  </td>
                  <td style={{ fontWeight: 500 }}>{tx.ingredientName}</td>
                  <td>
                    <span
                      className={`badge ${tx.type === 'restock' ? 'badge-success' : tx.type === 'sale' ? 'badge-muted' : 'badge-warning'}`}
                    >
                      {txTypeLabel(tx.type)}
                    </span>
                  </td>
                  <td
                    style={{
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color: tx.delta < 0 ? 'var(--danger)' : 'var(--success)',
                      fontWeight: 600,
                    }}
                  >
                    {tx.delta > 0 ? '+' : ''}
                    {fmtNum(tx.delta)} {tx.unit}
                  </td>
                  <td>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {tx.note ?? t('common.emptyDash')}
                    </span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                    {t('inventoryInsights.noTransactions')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {totalCount > PAGE_SIZE && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 0 4px',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className="btn-ghost btn-sm"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('common.prev')}
              </button>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {t('common.pageOf', { page: page + 1, count: pageCount })}
              </span>
              <button
                className="btn-ghost btn-sm"
                disabled={page + 1 >= pageCount}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Stock Flow tab ──────────────────────────────────────────────────────────

interface FlowRow {
  id: string;
  name: string;
  unit: string;
  stockIn: number;
  stockOut: number;
  net: number;
}

function StockFlowTab({ clientId }: { clientId: string | null }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<FlowPeriod>('7d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [rows, setRows] = useState<FlowRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  useEffect(() => {
    setPage(0);
  }, [period, customFrom, customTo, search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!supabase || !clientId) return;
    const db = supabase;
    if (period === 'custom' && (!customFrom || !customTo)) return;
    const { from, to } = getPeriodDates(period, customFrom, customTo);
    const fromIso = new Date(from).toISOString();
    const toEnd = new Date(to);
    toEnd.setHours(23, 59, 59, 999);
    const toIso = toEnd.toISOString();

    setLoading(true);
    setError(null);

    Promise.all([
      db.from('ingredients').select('id, name, unit').eq('client_id', clientId).order('name'),
      fetchPaged((offset, limit) =>
        db
          .from('inventory_transactions')
          .select('ingredient_id, type, delta')
          .eq('client_id', clientId)
          .gte('created_at', fromIso)
          .lte('created_at', toIso)
          .order('created_at', { ascending: true })
          .range(offset, limit),
      ),
    ])
      .then(([ingRes, txs]) => {
        if (ingRes.error) {
          setError(ingRes.error.message);
          setLoading(false);
          return;
        }

        const ings = ingRes.data ?? [];

        const ingMap = new Map(ings.map((i) => [i.id, { name: i.name, unit: i.unit ?? '' }]));

        const flowMap = new Map<string, { stockIn: number; stockOut: number }>();
        for (const tx of txs) {
          const id = tx.ingredient_id as string;
          if (!flowMap.has(id)) flowMap.set(id, { stockIn: 0, stockOut: 0 });
          const entry = flowMap.get(id)!;
          const delta = Number(tx.delta);
          if (delta > 0) entry.stockIn += delta;
          else entry.stockOut += Math.abs(delta);
        }

        const result: FlowRow[] = [];
        for (const [id, { stockIn, stockOut }] of flowMap) {
          const ing = ingMap.get(id);
          if (!ing) continue;
          result.push({
            id,
            name: ing.name,
            unit: ing.unit,
            stockIn,
            stockOut,
            net: stockIn - stockOut,
          });
        }
        result.sort((a, b) => a.name.localeCompare(b.name));
        setRows(result);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : t('inventoryInsights.loadFailed'));
        setLoading(false);
      });
  }, [clientId, period, customFrom, customTo, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = search.trim()
    ? rows.filter((r) => r.name.toLowerCase().includes(search.trim().toLowerCase()))
    : rows;
  const paged = visible.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="chart-card" style={{ marginTop: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 12,
        }}
      >
        <DateRange
          value={{ preset: period, from: customFrom, to: customTo }}
          onChange={(v) => {
            setPeriod(v.preset);
            setCustomFrom(v.from ?? '');
            setCustomTo(v.to ?? '');
          }}
        />
        <input
          type="search"
          placeholder={t('inventoryInsights.searchIngredient')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            marginLeft: 'auto',
            padding: '4px 10px',
            borderRadius: 'var(--r)',
            border: '1px solid var(--border)',
            background: 'var(--sb)',
            color: 'var(--text)',
            fontSize: 13,
          }}
        />
      </div>

      {loading && (
        <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
          {t('common.loading')}
        </div>
      )}
      {error && <div className="error-banner">{error}</div>}

      {!loading && (
        <table className="table">
          <thead>
            <tr>
              <th>{t('inventoryInsights.colIngredient')}</th>
              <th style={{ textAlign: 'right' }}>{t('inventoryInsights.colStockIn')}</th>
              <th style={{ textAlign: 'right' }}>{t('inventoryInsights.colStockOut')}</th>
              <th style={{ textAlign: 'right' }}>{t('inventoryInsights.colNet')}</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((r) => (
              <tr key={r.id}>
                <td style={{ fontWeight: 500 }}>{r.name}</td>
                <td
                  style={{
                    textAlign: 'right',
                    color: 'var(--success)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {r.stockIn > 0 ? (
                    `+${fmtNum(r.stockIn)} ${r.unit}`
                  ) : (
                    <span className="muted">{t('common.emptyDash')}</span>
                  )}
                </td>
                <td
                  style={{
                    textAlign: 'right',
                    color: r.stockOut > 0 ? 'var(--danger)' : 'var(--text-muted)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {r.stockOut > 0 ? (
                    `−${fmtNum(r.stockOut)} ${r.unit}`
                  ) : (
                    <span className="muted">{t('common.emptyDash')}</span>
                  )}
                </td>
                <td
                  style={{
                    textAlign: 'right',
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    color: r.net >= 0 ? 'var(--success)' : 'var(--danger)',
                  }}
                >
                  {r.net > 0 ? '+' : ''}
                  {fmtNum(r.net)} {r.unit}
                </td>
              </tr>
            ))}
            {visible.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={4}
                  style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 0' }}
                >
                  {period === 'custom' && (!customFrom || !customTo)
                    ? t('inventoryInsights.selectRange')
                    : t('inventoryInsights.noTxPeriod')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {visible.length > PAGE_SIZE && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 0 4px',
            justifyContent: 'flex-end',
          }}
        >
          <button
            className="btn-ghost btn-sm"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            {t('common.prev')}
          </button>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {t('common.pageOf', { page: page + 1, count: Math.ceil(visible.length / PAGE_SIZE) })}
          </span>
          <button
            className="btn-ghost btn-sm"
            disabled={(page + 1) * PAGE_SIZE >= visible.length}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Loss tab (waste + unknown count variances) ───────────────────────────────

type LossCategory = 'waste' | 'unknown';

interface LossRow {
  id: string;
  category: LossCategory;
  at: string;
  ingredientId: string;
  ingredientName: string;
  unit: string;
  delta: number;
  value: number;
  reason?: string;
  detail?: string;
  loggedBy?: string | null;
  systemQty?: number;
  countedQty?: number;
  revertedAt?: string | null;
  revertNote?: string | null;
  revertedBy?: string | null;
}

interface StockInRow {
  id: string;
  ingredientId: string;
  unit: string;
  qty: number;
  value: number;
  createdAt: string;
}

type LossChartMode = 'qty' | 'value';

function LossCompareChart({
  title,
  mode,
  onModeChange,
  stockIn,
  variance,
  unit,
  varianceLabel,
  stockColor = 'var(--info)',
  varianceColor = 'var(--danger)',
}: {
  title: string;
  mode: LossChartMode;
  onModeChange: (m: LossChartMode) => void;
  stockIn: number;
  variance: number;
  unit: string;
  varianceLabel: string;
  stockColor?: string;
  varianceColor?: string;
}) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const si = stockIn;
  const va = variance;
  const total = si + va;
  const stockPct = total > 0 ? (si / total) * 100 : 0;
  const varPct = total > 0 ? (va / total) * 100 : 0;
  const varOfStock = si > 0 ? (va / si) * 100 : 0;
  const mixedUnits = mode === 'qty' && !unit;

  const fmt = (n: number) =>
    mode === 'value' ? AMD(n) : `${fmtNum(n)}${unit ? ` ${unit}` : ''}`;

  const hasData = si > 0 || va > 0;

  return (
    <div className="loss-compare-chart">
      <div className="loss-compare-head">
        <span className="chart-title">{title}</span>
        <select
          className="input loss-compare-mode"
          value={mode}
          onChange={(e) => onModeChange(e.target.value as LossChartMode)}
        >
          <option value="qty">
            {unit
              ? `${t('inventoryInsights.modeQty')} (${unit})`
              : t('inventoryInsights.modeQty')}
          </option>
          <option value="value">{t('inventoryInsights.modeValue')}</option>
        </select>
      </div>

      {mixedUnits && hasData && (
        <p className="muted loss-compare-hint">{t('inventoryInsights.mixedUnits')}</p>
      )}

      {!hasData && (
        <p className="muted loss-compare-hint">{t('inventoryInsights.noDataPeriod')}</p>
      )}

      {hasData && (
        <>
          <div
            className="loss-compare-bar"
            role="img"
            aria-label={t('inventoryInsights.compareAria', {
              stockPct: stockPct.toFixed(0),
              label: varianceLabel,
              varPct: varPct.toFixed(0),
            })}
          >
            {si > 0 && (
              <div
                className="loss-compare-seg"
                style={{ width: `${stockPct}%`, background: stockColor }}
                title={t('inventoryInsights.stockInPct', { pct: stockPct.toFixed(0) })}
              />
            )}
            {va > 0 && (
              <div
                className="loss-compare-seg"
                style={{ width: `${varPct}%`, background: varianceColor }}
                title={`${varianceLabel} ${varPct.toFixed(0)}%`}
              />
            )}
          </div>
          <div className="loss-compare-rows">
            <div className="loss-compare-row">
              <span className="loss-compare-dot" style={{ background: stockColor }} />
              <span className="loss-compare-label">{t('inventoryInsights.stockIn')}</span>
              <span className="loss-compare-val">{fmt(si)}</span>
              <span className="loss-compare-pct">{stockPct.toFixed(0)}%</span>
            </div>
            <div className="loss-compare-row">
              <span className="loss-compare-dot" style={{ background: varianceColor }} />
              <span className="loss-compare-label">{varianceLabel}</span>
              <span className="loss-compare-val">{fmt(va)}</span>
              <span className="loss-compare-pct">{varPct.toFixed(0)}%</span>
            </div>
          </div>
          {si > 0 && va > 0 && (
            <p className="muted loss-compare-ratio">
              {t('inventoryInsights.varianceRatio', {
                label: varianceLabel,
                pct: varOfStock.toFixed(1),
              })}
            </p>
          )}
        </>
      )}
    </div>
  );
}

type LossTopItem = {
  id: string;
  name: string;
  unit: string;
  waste: number;
  unknown: number;
  total: number;
};

function LossKpiHero({
  stockInValue,
  grossLossValue,
  netLossValue,
  grossLossPct,
  netLossPct,
  wasteValue,
  unknownDownValue,
  unknownUpValue,
  wastePct,
  unknownDownPct,
}: {
  stockInValue: number;
  grossLossValue: number;
  netLossValue: number;
  grossLossPct: number;
  netLossPct: number;
  wasteValue: number;
  unknownDownValue: number;
  unknownUpValue: number;
  wastePct: number;
  unknownDownPct: number;
}) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const hasPurchases = stockInValue > 0;
  const hasLoss = grossLossValue > 0;

  return (
    <div className="loss-kpi-hero">
      <div className="loss-kpi-main">
        <span className="loss-kpi-eyebrow">{t('inventoryInsights.lossRate')}</span>
        <div className="loss-kpi-primary">
          <span
            className="loss-kpi-value"
            style={{
              color: hasLoss && grossLossPct > 5 ? 'var(--danger)' : hasLoss ? 'var(--warning)' : undefined,
            }}
          >
            {hasPurchases ? `${grossLossPct}%` : t('common.emptyDash')}
          </span>
          {hasLoss && (
            <span className="loss-kpi-net muted">
              {t('inventoryInsights.netLoss', { pct: netLossPct, amount: AMD(netLossValue) })}
            </span>
          )}
        </div>
        <p className="loss-kpi-formula muted">{t('inventoryInsights.formula')}</p>
        {hasPurchases && (
          <p className="loss-kpi-benchmark muted">
            {t('inventoryInsights.benchmark', { amount: AMD(stockInValue) })}
          </p>
        )}
      </div>
      <div className="loss-kpi-breakdown">
        <div className="loss-kpi-chip">
          <span className="loss-kpi-chip-label">{t('inventoryInsights.waste')}</span>
          <span className="loss-kpi-chip-val" style={{ color: wasteValue > 0 ? 'var(--danger)' : undefined }}>
            {wasteValue > 0 ? `−${AMD(wasteValue)}` : t('common.emptyDash')}
          </span>
          {hasPurchases && wasteValue > 0 && (
            <span className="muted loss-kpi-chip-pct">
              {t('inventoryInsights.ofStockIn', { pct: wastePct })}
            </span>
          )}
        </div>
        <div className="loss-kpi-chip">
          <span className="loss-kpi-chip-label">{t('inventoryInsights.unknownDown')}</span>
          <span className="loss-kpi-chip-val" style={{ color: unknownDownValue > 0 ? 'var(--danger)' : undefined }}>
            {unknownDownValue > 0 ? `−${AMD(unknownDownValue)}` : t('common.emptyDash')}
          </span>
          {hasPurchases && unknownDownValue > 0 && (
            <span className="muted loss-kpi-chip-pct">
              {t('inventoryInsights.ofStockIn', { pct: unknownDownPct })}
            </span>
          )}
        </div>
        <div className="loss-kpi-chip">
          <span className="loss-kpi-chip-label">{t('inventoryInsights.unknownUp')}</span>
          <span className="loss-kpi-chip-val" style={{ color: unknownUpValue > 0 ? 'var(--success)' : undefined }}>
            {unknownUpValue > 0 ? `+${AMD(unknownUpValue)}` : t('common.emptyDash')}
          </span>
          {unknownUpValue > 0 && (
            <span className="muted loss-kpi-chip-pct">{t('inventoryInsights.recovered')}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function LossWaterfall({
  stockIn,
  waste,
  unknownDown,
  unknownUp,
}: {
  stockIn: number;
  waste: number;
  unknownDown: number;
  unknownUp: number;
}) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const gross = waste + unknownDown;
  const net = Math.max(0, gross - unknownUp);
  const scale = Math.max(stockIn, gross, 1);

  const steps = [
    { key: 'stockIn', label: t('inventoryInsights.stockIn'), value: stockIn, tone: 'in' as const },
    { key: 'waste', label: t('inventoryInsights.waste'), value: waste, tone: 'out' as const },
    { key: 'unknownDown', label: t('inventoryInsights.unknownDown'), value: unknownDown, tone: 'out' as const },
    { key: 'unknownUp', label: t('inventoryInsights.unknownUp'), value: unknownUp, tone: 'credit' as const },
    { key: 'netLoss', label: t('inventoryInsights.netLossLabel'), value: net, tone: 'total' as const },
  ];

  if (stockIn <= 0 && gross <= 0 && unknownUp <= 0) {
    return null;
  }

  return (
    <div className="loss-waterfall">
      <span className="chart-title loss-waterfall-title">{t('inventoryInsights.periodFlow')}</span>
      <div className="loss-waterfall-steps">
        {steps.map((s) => {
          const width = scale > 0 ? (s.value / scale) * 100 : 0;
          const show = s.tone === 'in' || s.value > 0;
          if (!show) return null;
          return (
            <div key={s.key} className={`loss-waterfall-step loss-waterfall-${s.tone}`}>
              <div className="loss-waterfall-step-head">
                <span className="loss-waterfall-label">{s.label}</span>
                <span className="loss-waterfall-amount">
                  {s.tone === 'in' || s.tone === 'credit' ? '+' : s.tone === 'total' ? '−' : '−'}
                  {AMD(s.value)}
                  {s.tone !== 'in' && stockIn > 0 && (
                    <span className="muted loss-waterfall-pct"> · {lossPct(s.value, stockIn)}%</span>
                  )}
                </span>
              </div>
              <div className="loss-waterfall-track">
                <div className="loss-waterfall-fill" style={{ width: `${Math.max(width, s.value > 0 ? 2 : 0)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LossTrendChart({
  data,
}: {
  data: { label: string; waste: number; unknownDown: number; lossRate: number }[];
}) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const hasLoss = data.some((d) => d.waste + d.unknownDown > 0);
  const hasStockIn = data.some((d) => d.waste + d.unknownDown > 0 || d.lossRate > 0);

  if (!hasLoss && !hasStockIn) {
    return (
      <div className="loss-trend-card">
        <span className="chart-title">{t('inventoryInsights.dailyTrend')}</span>
        <p className="muted loss-compare-hint">{t('inventoryInsights.noLoss')}</p>
      </div>
    );
  }

  return (
    <div className="loss-trend-card">
      <div className="loss-trend-head">
        <span className="chart-title">{t('inventoryInsights.dailyTrend')}</span>
        <span className="muted loss-trend-legend">{t('inventoryInsights.trendLegend')}</span>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} interval="preserveStartEnd" />
          <YAxis
            yAxisId="value"
            tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
            tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
            width={44}
          />
          <YAxis
            yAxisId="rate"
            orientation="right"
            tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
            tickFormatter={(v: number) => `${v}%`}
            width={36}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const p = payload[0]?.payload as (typeof data)[0] | undefined;
              if (!p) return null;
              const loss = p.waste + p.unknownDown;
              return (
                <div className="chart-tooltip">
                  <div className="chart-tooltip-title">{label}</div>
                  <div>{t('inventoryInsights.tooltipWaste', { amount: AMD(p.waste) })}</div>
                  <div>{t('inventoryInsights.tooltipUnknownDown', { amount: AMD(p.unknownDown) })}</div>
                  <div style={{ fontWeight: 600 }}>
                    {t('inventoryInsights.tooltipTotalLoss', { amount: AMD(loss) })}
                  </div>
                  <div className="muted">
                    {t('inventoryInsights.tooltipLossRate', { pct: p.lossRate })}
                  </div>
                </div>
              );
            }}
          />
          <Bar
            yAxisId="value"
            dataKey="waste"
            stackId="loss"
            fill="var(--warning)"
            name={t('inventoryInsights.waste')}
            radius={[0, 0, 0, 0]}
          />
          <Bar
            yAxisId="value"
            dataKey="unknownDown"
            stackId="loss"
            fill="var(--danger)"
            name={t('inventoryInsights.unknownDown')}
            radius={[2, 2, 0, 0]}
          />
          <Line
            yAxisId="rate"
            type="monotone"
            dataKey="lossRate"
            stroke="var(--info)"
            strokeWidth={2}
            dot={false}
            name={t('inventoryInsights.lossRate')}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function LossRankList({
  title,
  subtitle,
  emptyLabel,
  rows,
}: {
  title: string;
  subtitle?: string;
  emptyLabel: string;
  rows: { key: string; label: string; sublabel?: string; value: number; pct?: number }[];
}) {
  const { i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const max = rows[0]?.value ?? 0;

  return (
    <div className="loss-rank-card">
      <div className="loss-rank-head">
        <span className="chart-title">{title}</span>
        {subtitle && <span className="muted loss-rank-sub">{subtitle}</span>}
      </div>
      {rows.length === 0 ? (
        <p className="muted loss-compare-hint">{emptyLabel}</p>
      ) : (
        <ol className="loss-rank-list">
          {rows.map((r, i) => (
            <li key={r.key} className="loss-rank-item">
              <span className="loss-rank-rank">{i + 1}</span>
              <div className="loss-rank-body">
                <div className="loss-rank-meta">
                  <span className="loss-rank-label">{r.label}</span>
                  {r.sublabel && <span className="muted loss-rank-sublabel">{r.sublabel}</span>}
                </div>
                <div className="loss-rank-bar-wrap">
                  <div
                    className="loss-rank-bar"
                    style={{ width: max > 0 ? `${(r.value / max) * 100}%` : '0%' }}
                  />
                </div>
              </div>
              <span className="loss-rank-val">
                −{AMD(r.value)}
                {r.pct != null && r.pct > 0 && <span className="muted loss-rank-pct">{r.pct}%</span>}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function LossInsightsTab({ clientId }: { clientId: string | null }) {
  const { t, i18n } = useTranslation();
  const locale = resolveAppLocale(i18n.language);
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const reasonLabel = (reason: string) => t(`wasteReasons.${reason}`, { defaultValue: reason });
  const [period, setPeriod] = useState<FlowPeriod>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [ingredientId, setIngredientId] = useState('');
  const [category, setCategory] = useState<'all' | LossCategory>('all');
  const [ingredients, setIngredients] = useState<
    { id: string; name: string; unit: string; cost_price: number; stock: number }[]
  >([]);
  const [rows, setRows] = useState<LossRow[]>([]);
  const [stockIns, setStockIns] = useState<StockInRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [downChartMode, setDownChartMode] = useState<LossChartMode>('value');
  const [upChartMode, setUpChartMode] = useState<LossChartMode>('value');
  const PAGE_SIZE = 25;
  const [refreshKey, setRefreshKey] = useState(0);
  const [editing, setEditing] = useState<LossRow | null>(null);
  const [editQty, setEditQty] = useState('');
  const [editReason, setEditReason] = useState<WasteReason>('other');
  const [editDetail, setEditDetail] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<LossRow | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    setPage(0);
  }, [period, customFrom, customTo, ingredientId, category]);

  useEffect(() => {
    if (!ingredientId) {
      setDownChartMode('value');
      setUpChartMode('value');
    }
  }, [ingredientId]);

  useEffect(() => {
    if (!supabase || !clientId) return;
    const db = supabase;
    const range = periodToIsoRange(period, customFrom, customTo);
    if (!range) return;

    setLoading(true);
    setError(null);

    Promise.all([
      db.from('ingredients').select('id, name, unit, cost_price, stock').eq('client_id', clientId).order('name'),
      fetchPaged((offset, limit) =>
        db
          .from('stock_counts')
          .select('id, counted_at')
          .eq('client_id', clientId)
          .gte('counted_at', range.fromIso)
          .lte('counted_at', range.toIso)
          .order('counted_at', { ascending: false })
          .range(offset, limit),
      ),
      fetchPaged((offset, limit) =>
        db
          .from('inventory_transactions')
          .select('id, ingredient_id, delta, type, invoice_id, reference_id, created_at')
          .eq('client_id', clientId)
          .in('type', ['invoice', 'restock'])
          .gt('delta', 0)
          .gte('created_at', range.fromIso)
          .lte('created_at', range.toIso)
          .order('created_at', { ascending: true })
          .range(offset, limit),
      ),
      fetchPaged((offset, limit) =>
        db
          .from('inventory_transactions')
          .select('id, ingredient_id, delta, note, created_at, created_by, reverted_at')
          .eq('client_id', clientId)
          .eq('type', 'waste')
          .gte('created_at', range.fromIso)
          .lte('created_at', range.toIso)
          .order('created_at', { ascending: false })
          .range(offset, limit),
      ),
    ]).then(async ([ingRes, counts, stockInTxs, wasteTxs]) => {
      if (ingRes.error) {
        setError(ingRes.error.message);
        setLoading(false);
        return;
      }

      const ings = ingRes.data ?? [];
      setIngredients(ings);
      const ingMap = new Map(ings.map((i) => [i.id, i]));
      const countMap = new Map(counts.map((c) => [c.id, c]));

      const creatorIds = [...new Set(wasteTxs.map((t) => t.created_by).filter(Boolean))] as string[];
      const profileMap = new Map<string, string>();
      if (creatorIds.length > 0) {
        const { data: profiles } = await db
          .from('profiles')
          .select('id, username, full_name')
          .in('id', creatorIds);
        for (const p of profiles ?? []) {
          profileMap.set(p.id, p.full_name ?? p.username ?? p.id);
        }
      }

      const purchaseStockInTxs = stockInTxs.filter(
        (t) =>
          t.type === 'invoice' ||
          (t.type === 'restock' && t.reference_id != null && isUuid(t.reference_id)),
      );

      const invoiceIds = [
        ...new Set(
          purchaseStockInTxs.map((t) => t.invoice_id).filter((id): id is string => !!id && isUuid(id)),
        ),
      ];
      const poIds = [
        ...new Set(
          purchaseStockInTxs
            .filter((t) => t.type === 'restock' && t.reference_id)
            .map((t) => t.reference_id!)
            .filter(isUuid),
        ),
      ];

      const [goodsRes, poiRes] = await Promise.all([
        invoiceIds.length > 0
          ? db
              .from('invoice_goods')
              .select('invoice_id, ingredient_id, total')
              .eq('client_id', clientId)
              .in('invoice_id', invoiceIds)
          : Promise.resolve({ data: [] as { invoice_id: string; ingredient_id: string | null; total: number }[], error: null }),
        poIds.length > 0
          ? db
              .from('purchase_order_items')
              .select('purchase_order_id, ingredient_id, quantity, unit_cost')
              .eq('client_id', clientId)
              .in('purchase_order_id', poIds)
          : Promise.resolve({
              data: [] as {
                purchase_order_id: string;
                ingredient_id: string;
                quantity: number;
                unit_cost: number;
              }[],
              error: null,
            }),
      ]);

      if (goodsRes.error) {
        setError(goodsRes.error.message);
        setLoading(false);
        return;
      }
      if (poiRes.error) {
        setError(poiRes.error.message);
        setLoading(false);
        return;
      }

      const invoiceValueMap = new Map<string, number>();
      for (const g of goodsRes.data ?? []) {
        if (!g.ingredient_id) continue;
        invoiceValueMap.set(`${g.invoice_id}:${g.ingredient_id}`, Number(g.total));
      }
      const poValueMap = new Map<string, number>();
      for (const p of poiRes.data ?? []) {
        poValueMap.set(
          `${p.purchase_order_id}:${p.ingredient_id}`,
          Number(p.quantity) * Number(p.unit_cost),
        );
      }

      let stockInRows: StockInRow[] = purchaseStockInTxs.map((tx) => {
        const ing = ingMap.get(tx.ingredient_id);
        const qty = Number(tx.delta);
        const costPrice = Number(ing?.cost_price ?? 0);
        let value = qty * costPrice;
        if (tx.type === 'invoice' && tx.invoice_id && isUuid(tx.invoice_id)) {
          const v = invoiceValueMap.get(`${tx.invoice_id}:${tx.ingredient_id}`);
          if (v != null) value = v;
        } else if (tx.type === 'restock' && tx.reference_id && isUuid(tx.reference_id)) {
          const v = poValueMap.get(`${tx.reference_id}:${tx.ingredient_id}`);
          if (v != null) value = v;
        }
        return {
          id: tx.id,
          ingredientId: tx.ingredient_id,
          unit: ing?.unit ?? '',
          qty,
          value,
          createdAt: tx.created_at,
        };
      });

      let lossRows: LossRow[] = wasteTxs.map((tx) => {
        const ing = ingMap.get(tx.ingredient_id);
        const costPrice = Number(ing?.cost_price ?? 0);
        const delta = Number(tx.delta);
        const parsed = parseWasteNote(tx.note);
        return {
          id: tx.id,
          category: 'waste' as const,
          at: tx.created_at,
          ingredientId: tx.ingredient_id,
          ingredientName: ing?.name ?? t('common.unknown'),
          unit: ing?.unit ?? '',
          delta,
          value: delta * costPrice,
          reason: parsed.reason,
          detail: parsed.detail,
          loggedBy: tx.created_by ? (profileMap.get(tx.created_by) ?? null) : null,
          revertedAt: tx.reverted_at,
        };
      });

      if (counts.length > 0) {
        const countIds = counts.map((c) => c.id);
        const lines: Array<{
          id: string;
          stock_count_id: string;
          ingredient_id: string;
          system_qty: number;
          counted_qty: number;
          delta: number;
          reverted_at: string | null;
          revert_note: string | null;
          reverted_by: string | null;
        }> = [];

        for (let i = 0; i < countIds.length; i += 100) {
          const chunk = countIds.slice(i, i + 100);
          const batch = await fetchPaged((offset, limit) =>
            db
              .from('stock_count_lines')
              .select(
                'id, stock_count_id, ingredient_id, system_qty, counted_qty, delta, reverted_at, revert_note, reverted_by',
              )
              .eq('client_id', clientId)
              .in('stock_count_id', chunk)
              .order('id')
              .range(offset, limit),
          );
          lines.push(...batch);
        }

        const unknownRows: LossRow[] = lines.map((line) => {
          const count = countMap.get(line.stock_count_id)!;
          const ing = ingMap.get(line.ingredient_id);
          const costPrice = Number(ing?.cost_price ?? 0);
          const delta = Number(line.delta);
          return {
            id: line.id,
            category: 'unknown' as const,
            at: count.counted_at,
            ingredientId: line.ingredient_id,
            ingredientName: ing?.name ?? t('common.unknown'),
            unit: ing?.unit ?? '',
            delta,
            value: delta * costPrice,
            systemQty: Number(line.system_qty),
            countedQty: Number(line.counted_qty),
            revertedAt: line.reverted_at,
            revertNote: line.revert_note,
            revertedBy: line.reverted_by,
          };
        });
        lossRows = lossRows.concat(unknownRows);
      }

      if (ingredientId) {
        lossRows = lossRows.filter((r) => r.ingredientId === ingredientId);
        stockInRows = stockInRows.filter((r) => r.ingredientId === ingredientId);
      }

      lossRows.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

      setStockIns(stockInRows);
      setRows(lossRows);
      setLoading(false);
    });
  }, [clientId, period, customFrom, customTo, ingredientId, refreshKey]);

  function startEdit(row: LossRow) {
    setEditing(row);
    setEditQty(String(Math.abs(row.delta)));
    setEditReason(normalizeWasteReason(row.reason));
    setEditDetail(row.detail ?? '');
    setEditError(null);
  }

  async function saveEdit() {
    if (!supabase || !editing) return;
    const qty = Number(editQty);
    if (!Number.isFinite(qty) || qty <= 0) {
      setEditError(t('wasteLog.positiveQty'));
      return;
    }
    if (editReason === 'other' && !editDetail.trim()) {
      setEditError(t('wasteLog.commentRequired'));
      return;
    }
    setEditSaving(true);
    setEditError(null);
    const oldQty = Math.abs(editing.delta);
    const { data: ing, error: loadErr } = await supabase
      .from('ingredients')
      .select('stock')
      .eq('id', editing.ingredientId)
      .single();
    if (loadErr || !ing) {
      setEditError(loadErr?.message ?? t('common.unknown'));
      setEditSaving(false);
      return;
    }
    const nextStock = Math.max(0, Number(ing.stock) + oldQty - qty);
    const { error: stockErr } = await supabase
      .from('ingredients')
      .update({ stock: nextStock })
      .eq('id', editing.ingredientId);
    if (stockErr) {
      setEditError(stockErr.message);
      setEditSaving(false);
      return;
    }
    const detail = editDetail.trim();
    const note = detail ? `${editReason} — ${detail}` : editReason;
    const { error: txErr } = await supabase
      .from('inventory_transactions')
      .update({ delta: -qty, note })
      .eq('id', editing.id);
    if (txErr) {
      await supabase.from('ingredients').update({ stock: Number(ing.stock) }).eq('id', editing.ingredientId);
      setEditError(txErr.message);
      setEditSaving(false);
      return;
    }
    setEditSaving(false);
    setEditing(null);
    setRefreshKey((key) => key + 1);
  }

  async function cancelWaste() {
    if (!supabase || !confirmCancel) return;
    setCancelling(true);
    setError(null);
    const { error: err } = await supabase.rpc('revert_waste_transaction', { p_tx_id: confirmCancel.id });
    setCancelling(false);
    if (err) {
      setError(err.message);
      return;
    }
    setConfirmCancel(null);
    setRefreshKey((key) => key + 1);
  }

  const activeRows = rows.filter((r) => !r.revertedAt);
  const viewActiveRows =
    category === 'all' ? activeRows : activeRows.filter((r) => r.category === category);
  const wasteRows = viewActiveRows.filter((r) => r.category === 'waste');
  const unknownRows = viewActiveRows.filter((r) => r.category === 'unknown');
  const filteredRows =
    category === 'all' ? rows : rows.filter((r) => r.category === category);

  const totalStockInQty = stockIns.reduce((s, r) => s + r.qty, 0);
  const totalStockInValue = stockIns.reduce((s, r) => s + r.value, 0);
  const wasteValue = wasteRows.reduce((s, r) => s + Math.abs(r.value), 0);
  const wasteQty = wasteRows.reduce((s, r) => s + Math.abs(r.delta), 0);
  const unknownDownValue = unknownRows.reduce(
    (s, r) => (r.value < 0 ? s + Math.abs(r.value) : s),
    0,
  );
  const unknownUpValue = unknownRows.reduce((s, r) => (r.value > 0 ? s + r.value : s), 0);
  const unknownDownQty = unknownRows.reduce(
    (s, r) => (r.delta < 0 ? s + Math.abs(r.delta) : s),
    0,
  );
  const unknownUpQty = unknownRows.reduce((s, r) => (r.delta > 0 ? s + r.delta : s), 0);
  const lossDownQty = wasteQty + unknownDownQty;
  const lossDownValue = wasteValue + unknownDownValue;
  const grossLossValue = lossDownValue;
  const netLossValue = Math.max(0, grossLossValue - unknownUpValue);
  const grossLossPct = lossPct(grossLossValue, totalStockInValue);
  const netLossPct = lossPct(netLossValue, totalStockInValue);
  const wastePctOfStockIn = lossPct(wasteValue, totalStockInValue);
  const unknownDownPctOfStockIn = lossPct(unknownDownValue, totalStockInValue);

  const trendData = useMemo(() => {
    const { from, to } = getPeriodDates(period, customFrom, customTo);
    const days = enumerateDays(from, to);
    const byKey = new Map(
      days.map((d) => [d.key, { label: d.label, stockIn: 0, waste: 0, unknownDown: 0 }]),
    );
    for (const s of stockIns) {
      const b = byKey.get(toDayKey(s.createdAt));
      if (b) b.stockIn += s.value;
    }
    for (const r of viewActiveRows) {
      const b = byKey.get(toDayKey(r.at));
      if (!b) continue;
      if (r.category === 'waste') b.waste += Math.abs(r.value);
      else if (r.value < 0) b.unknownDown += Math.abs(r.value);
    }
    return days.map((d) => {
      const b = byKey.get(d.key)!;
      const loss = b.waste + b.unknownDown;
      const dayDate = new Date(d.key + 'T12:00:00');
      return {
        label: dayDate.toLocaleDateString(locale, { day: '2-digit', month: 'short' }),
        stockIn: b.stockIn,
        waste: b.waste,
        unknownDown: b.unknownDown,
        loss,
        lossRate: lossPct(loss, b.stockIn),
      };
    });
  }, [stockIns, viewActiveRows, period, customFrom, customTo, category, locale]);

  const topLossItems = useMemo((): LossTopItem[] => {
    const m = new Map<string, LossTopItem>();
    for (const r of viewActiveRows) {
      let loss = 0;
      if (r.category === 'waste') loss = Math.abs(r.value);
      else if (r.value < 0) loss = Math.abs(r.value);
      else continue;
      const e = m.get(r.ingredientId) ?? {
        id: r.ingredientId,
        name: r.ingredientName,
        unit: r.unit,
        waste: 0,
        unknown: 0,
        total: 0,
      };
      if (r.category === 'waste') e.waste += loss;
      else e.unknown += loss;
      e.total = e.waste + e.unknown;
      m.set(r.ingredientId, e);
    }
    return [...m.values()].sort((a, b) => b.total - a.total).slice(0, 8);
  }, [viewActiveRows, category]);

  const wasteByReason = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of wasteRows) {
      const reason = r.reason ?? 'other';
      m.set(reason, (m.get(reason) ?? 0) + Math.abs(r.value));
    }
    return [...m.entries()]
      .map(([reason, value]) => ({ reason, value }))
      .sort((a, b) => b.value - a.value);
  }, [wasteRows]);

  const qtyUnits = new Set([
    ...stockIns.map((r) => r.unit).filter(Boolean),
    ...rows.map((r) => r.unit).filter(Boolean),
  ]);
  const summaryUnit: string =
    ingredientId && (stockIns[0]?.unit || rows[0]?.unit)
      ? (stockIns[0]?.unit || rows[0]?.unit || '')
      : qtyUnits.size === 1
        ? ([...qtyUnits][0] ?? '')
        : '';

  const paged = filteredRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <div className="chart-card" style={{ marginTop: 0 }}>
      <PeriodFilters
        period={period}
        setPeriod={setPeriod}
        customFrom={customFrom}
        setCustomFrom={setCustomFrom}
        customTo={customTo}
        setCustomTo={setCustomTo}
        ingredientId={ingredientId}
        setIngredientId={setIngredientId}
        ingredients={ingredients}
        extra={
          <div className="segment-ctrl">
            {(
              [
                ['all', t('inventoryInsights.categoryAll')],
                ['waste', t('inventoryInsights.categoryWaste')],
                ['unknown', t('inventoryInsights.categoryUnknown')],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={category === key ? 'active' : ''}
                onClick={() => setCategory(key)}
              >
                {label}
              </button>
            ))}
          </div>
        }
      />

      <LossKpiHero
        stockInValue={totalStockInValue}
        grossLossValue={grossLossValue}
        netLossValue={netLossValue}
        grossLossPct={grossLossPct}
        netLossPct={netLossPct}
        wasteValue={wasteValue}
        unknownDownValue={unknownDownValue}
        unknownUpValue={unknownUpValue}
        wastePct={wastePctOfStockIn}
        unknownDownPct={unknownDownPctOfStockIn}
      />

      <LossWaterfall
        stockIn={totalStockInValue}
        waste={wasteValue}
        unknownDown={unknownDownValue}
        unknownUp={unknownUpValue}
      />

      <div className="loss-analytics-grid">
        <LossTrendChart data={trendData} />
        <LossRankList
          title={t('inventoryInsights.topLoss')}
          subtitle={
            category === 'waste'
              ? t('inventoryInsights.byValueWaste')
              : category === 'unknown'
                ? t('inventoryInsights.byValueUnknown')
                : t('inventoryInsights.byValueAll')
          }
          emptyLabel={
            category === 'waste'
              ? t('inventoryInsights.noLoggedWaste')
              : category === 'unknown'
                ? t('inventoryInsights.noUnknown')
                : t('inventoryInsights.noLossItems')
          }
          rows={topLossItems.map((item) => ({
            key: item.id,
            label: item.name,
            sublabel: [
              item.waste > 0 ? t('inventoryInsights.wasteAmount', { amount: AMD(item.waste) }) : '',
              item.unknown > 0 ? t('inventoryInsights.unknownAmount', { amount: AMD(item.unknown) }) : '',
            ]
              .filter(Boolean)
              .join(' · '),
            value: item.total,
            pct: lossPct(item.total, totalStockInValue),
          }))}
        />
      </div>

      <div className="loss-analytics-grid">
        <LossRankList
          title={t('inventoryInsights.wasteByReason')}
          subtitle={
            category === 'unknown'
              ? t('inventoryInsights.wasteFilterOff')
              : t('inventoryInsights.wasteOnly')
          }
          emptyLabel={
            category === 'unknown'
              ? t('inventoryInsights.selectWasteOrAll')
              : t('inventoryInsights.noLoggedWaste')
          }
          rows={wasteByReason.map(({ reason, value }) => ({
            key: reason,
            label: reasonLabel(reason),
            value,
            pct: lossPct(value, totalStockInValue),
          }))}
        />
        <div className="loss-compare-grid loss-compare-grid--nested">
          <LossCompareChart
            title={t('inventoryInsights.vsDown')}
            mode={downChartMode}
            onModeChange={setDownChartMode}
            stockIn={downChartMode === 'value' ? totalStockInValue : totalStockInQty}
            variance={downChartMode === 'value' ? lossDownValue : lossDownQty}
            unit={summaryUnit}
            varianceLabel={t('inventoryInsights.varianceDown')}
            varianceColor="var(--danger)"
          />
          <LossCompareChart
            title={t('inventoryInsights.vsUp')}
            mode={upChartMode}
            onModeChange={setUpChartMode}
            stockIn={upChartMode === 'value' ? totalStockInValue : totalStockInQty}
            variance={upChartMode === 'value' ? unknownUpValue : unknownUpQty}
            unit={summaryUnit}
            varianceLabel={t('inventoryInsights.varianceUp')}
            varianceColor="var(--success)"
          />
        </div>
      </div>

      {loading && <div style={{ padding: 16, color: 'var(--text-muted)' }}>{t('common.loading')}</div>}
      {error && <div className="error-banner">{error}</div>}

      {!loading && (
        <>
          <h3 className="chart-title loss-events-title">
            {category === 'all'
              ? t('inventoryInsights.lossEvents')
              : category === 'waste'
                ? t('inventoryInsights.wasteEvents')
                : t('inventoryInsights.unknownEvents')}
          </h3>
          <table className="table">
          <thead>
            <tr>
              <th>{t('inventoryInsights.colWhen')}</th>
              <th>{t('inventoryInsights.colCategory')}</th>
              <th>{t('inventoryInsights.colIngredient')}</th>
              <th style={{ textAlign: 'right' }}>{t('inventoryInsights.colDelta')}</th>
              <th style={{ textAlign: 'right' }}>{t('inventoryInsights.colValue')}</th>
              <th>{t('inventoryInsights.colDetails')}</th>
              <th aria-label={t('common.actions')} />
            </tr>
          </thead>
          <tbody>
            {paged.map((r) => (
              <tr key={r.id} style={r.revertedAt ? { opacity: 0.55 } : undefined}>
                <td className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  {fmtDtLocal(r.at, locale)}
                </td>
                <td>
                  <span className={`badge ${r.category === 'waste' ? 'badge-warning' : 'badge-muted'}`}>
                    {r.revertedAt
                      ? t('inventoryInsights.catReverted')
                      : r.category === 'waste'
                        ? t('inventoryInsights.catWaste')
                        : t('inventoryInsights.catUnknown')}
                  </span>
                </td>
                <td style={{ fontWeight: 500 }}>
                  {r.ingredientName}
                  {r.unit && (
                    <span className="muted" style={{ fontWeight: 400, marginLeft: 6, fontSize: 12 }}>
                      ({r.unit})
                    </span>
                  )}
                </td>
                <td
                  style={{
                    textAlign: 'right',
                    fontWeight: 600,
                    color: r.delta < 0 ? 'var(--danger)' : r.delta > 0 ? 'var(--success)' : 'var(--text-muted)',
                  }}
                >
                  {r.delta > 0 ? '+' : ''}
                  {fmtNum(r.delta)} {r.unit}
                </td>
                <td
                  style={{
                    textAlign: 'right',
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    color:
                      r.value < 0
                        ? 'var(--danger)'
                        : r.value > 0
                          ? 'var(--success)'
                          : 'var(--text-muted)',
                  }}
                >
                  {r.value === 0 ? (
                    t('common.emptyDash')
                  ) : (
                    <>
                      {r.value > 0 ? '+' : '−'}
                      {AMD(Math.abs(r.value))}
                    </>
                  )}
                </td>
                <td className="muted" style={{ fontSize: 12 }}>
                  {r.category === 'waste' ? (
                    <>
                      {r.reason ? reasonLabel(r.reason) : ''}
                      {r.detail ? ` — ${r.detail}` : ''}
                      {r.loggedBy ? ` · ${r.loggedBy}` : ''}
                    </>
                  ) : r.revertedAt ? (
                    <>
                      {t('inventoryInsights.wasSystemCounted', {
                        system: fmtNum(r.systemQty!),
                        counted: fmtNum(r.countedQty!),
                        unit: r.unit,
                      })}
                      {r.revertNote ? ` · ${r.revertNote}` : ''}
                    </>
                  ) : (
                    <>
                      {t('inventoryInsights.systemCounted', {
                        system: fmtNum(r.systemQty!),
                        counted: fmtNum(r.countedQty!),
                        unit: r.unit,
                      })}
                    </>
                  )}
                </td>
                <td>
                  {r.category === 'waste' && !r.revertedAt && (
                    <div className="ui-row-actions">
                      <IconButton label={t('common.edit')} size="sm" onClick={() => startEdit(r)}>
                        <Pencil className="ui-i" />
                      </IconButton>
                      <IconButton
                        label={t('wasteLog.cancelEntry')}
                        size="sm"
                        danger
                        onClick={() => setConfirmCancel(r)}
                      >
                        <X className="ui-i" />
                      </IconButton>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  {t('inventoryInsights.noLossData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </>
      )}
      {filteredRows.length > PAGE_SIZE && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8 }}>
          <button className="btn-ghost btn-sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            {t('common.prev')}
          </button>
          <span className="muted" style={{ fontSize: 13 }}>
            {t('common.pageOf', {
              page: page + 1,
              count: Math.ceil(filteredRows.length / PAGE_SIZE),
            })}
          </span>
          <button
            className="btn-ghost btn-sm"
            disabled={(page + 1) * PAGE_SIZE >= filteredRows.length}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('common.next')}
          </button>
        </div>
      )}
      <Modal
        open={editing != null}
        onClose={() => {
          if (!editSaving) setEditing(null);
        }}
        title={t('wasteLog.editTitle')}
        caption={editing?.ingredientName}
        footer={
          <>
            <Button variant="secondary" disabled={editSaving} onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" disabled={editSaving} onClick={() => void saveEdit()}>
              {editSaving ? '…' : t('common.save')}
            </Button>
          </>
        }
      >
        {editError && <Callout tone="danger">{editError}</Callout>}
        <Field label={t('wasteLog.quantity')}>
          <input
            type="number"
            min="0.001"
            step="any"
            value={editQty}
            onChange={(e) => setEditQty(e.target.value)}
          />
        </Field>
        <Field label={t('wasteLog.reason')}>
          <select value={editReason} onChange={(e) => setEditReason(e.target.value as WasteReason)}>
            {WASTE_REASONS.map((reason) => (
              <option key={reason} value={reason}>
                {reasonLabel(reason)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('wasteLog.note')} optional={editReason !== 'other'}>
          <input value={editDetail} onChange={(e) => setEditDetail(e.target.value)} />
        </Field>
      </Modal>
      <ConfirmDialog
        open={confirmCancel != null}
        onClose={() => {
          if (!cancelling) setConfirmCancel(null);
        }}
        onConfirm={() => void cancelWaste()}
        title={t('wasteLog.cancelTitle')}
        message={
          confirmCancel
            ? t('wasteLog.cancelMessage', {
                qty: fmtNum(Math.abs(confirmCancel.delta)),
                unit: confirmCancel.unit,
                name: confirmCancel.ingredientName,
              })
            : undefined
        }
        confirmLabel={cancelling ? t('wasteLog.cancelling') : t('wasteLog.cancelEntry')}
        busy={cancelling}
      />
    </div>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

function readInsightsTab(): InvTab {
  const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  const tab = params.get('tab');
  if (tab === 'waste' || tab === 'variances' || tab === 'loss') return 'loss';
  if (tab === 'flow' || tab === 'transactions' || tab === 'stock') return tab;
  return 'loss';
}

export function InventoryInsightsView({ clientId }: { clientId: string | null }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<InvTab>(() => readInsightsTab());
  const [data, setData] = useState<InvData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    fetchInventory(clientId)
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : t('inventoryInsights.loadFailed');
        setError(msg === 'Not configured' ? t('inventoryInsights.notConfigured') : msg);
        setLoading(false);
      });
  }, [clientId, t]);

  useEffect(() => {
    const handler = () => setTab(readInsightsTab());
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  function selectTab(next: InvTab) {
    const base = window.location.hash.split('?')[0];
    window.history.replaceState(null, '', `${base}?tab=${next}`);
    setTab(next);
  }

  return (
    <div className="sales-page">
      <PageHeader title={t('inventoryInsights.title')} subtitle={t('inventoryInsights.subtitle')} />

      <Tabs
        value={tab}
        onChange={selectTab}
        options={[
          { value: 'loss', label: t('inventoryInsights.tabLoss') },
          { value: 'stock', label: t('inventoryInsights.tabStock') },
          { value: 'transactions', label: t('inventoryInsights.tabTransactions') },
          { value: 'flow', label: t('inventoryInsights.tabFlow') },
        ]}
      />

      {loading && tab !== 'loss' && tab !== 'flow' && tab !== 'transactions' && (
        <div className="dash-loading">
          <div className="spinner" /> {t('common.loading')}
        </div>
      )}
      {error && tab !== 'loss' && tab !== 'flow' && tab !== 'transactions' && (
        <Callout tone="danger" title={t('inventoryInsights.couldntLoad')}>
          {error}
        </Callout>
      )}
      {!loading && data && tab === 'stock' && (
        <StockHealthTab ingredients={data.ingredients} depletion={data.depletion} />
      )}
      {tab === 'transactions' && <TransactionsTab clientId={clientId} />}
      {tab === 'flow' && <StockFlowTab clientId={clientId} />}
      {tab === 'loss' && <LossInsightsTab clientId={clientId} />}
    </div>
  );
}
